import {
    SpacingToken,
    UiStack,
} from '@experiences/ui-common';
import TextField from '@mui/material/TextField';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import { ApTextField } from '@uipath/portal-shell-react';
import React, { useCallback } from 'react';
import type { ControllerRenderProps } from 'react-hook-form';
import { useIntl } from 'react-intl';

import type {
    ByoLlmConfiguration,
    ByoOperationGroupConfiguration,
    LlmConnectorType,
} from '../../../../services/llm-gateway/LlmGateway.types';
import {
    IntegrationServiceConnectorTypesToAuthConnectorTypes,
    IntegrationServiceConnectorTypesToLlmConnectorTypes,
    ModelFamily,
} from '../../../../services/llm-gateway/LlmGateway.types';
import {
    createConfigurationWithAlternative,
    getApiFlavorsForAlternative,
    getApiFlavorsForModel,
    getAvailableDialectsForModel,
    getCurrentSelectedAlternative,
    getLlmConnectorTypeIntegrationServiceEquivalent,
    hasModelAlternatives,
    isAiGatewayCustomAuthConnector,
} from '../../../../services/llm-gateway/LlmGateway.utils';
import type { AddEditConfigurationFormData } from '../AddEditConfigurationViewModel';
import { AiGatewayDialectDropdown } from './AiGatewayDialectDropdown';
import { AvailableModelsAutocomplete } from './AvailableModelsAutocomplete';
import { ConnectionDropdown } from './ConnectionsDropdown';
import { ConnectorDropdown } from './ConnectorDropdown';
import { LlmApiFlavorDropdown } from './LlmApiFlavorDropdown';
import { ModelAlternativeDropdown } from './ModelAlternativeDropdown';

const useStyles = makeStyles(theme =>
    createStyles({
        llmConfigurationsSection: {
            width: '400px',
            borderLeft: `2px solid ${theme.palette.semantic.colorBackgroundDisabled}`,
        },
    }),
);

type AllModelsConfigurationSectionProps = {
    organizationId: string;
    tenantId: string;
    folderKey?: string;
    useAiGateway?: boolean;
    enableByoForPassthrough: boolean;
    llmConfigurationsField: ControllerRenderProps<AddEditConfigurationFormData, 'llmConfigurations'>;
    notifyFieldChanges: () => void;
    llmToConnectorTypeMapping: Record<string, LlmConnectorType[]>;
    byoAiGatewayCustomConnectors: string[];
    product: string;
    feature: string;
    productConfigurations?: Record<string, Record<string, ByoOperationGroupConfiguration>>;
    readOnly?: boolean;
};

export const AllModelsConfigurationSection: React.FC<AllModelsConfigurationSectionProps> = ({
    organizationId,
    tenantId,
    folderKey,
    useAiGateway,
    enableByoForPassthrough,
    llmConfigurationsField,
    notifyFieldChanges,
    llmToConnectorTypeMapping,
    byoAiGatewayCustomConnectors,
    product,
    feature,
    productConfigurations,
    readOnly,
}: AllModelsConfigurationSectionProps) => {
    const { formatMessage: translate } = useIntl();
    const classes = useStyles();

    const handleModelAlternativeChange = useCallback((desiredModel: string, llmConfiguration: Partial<ByoLlmConfiguration>) =>
        (selectedAlternative: string) => {
            const updatedConfiguration = createConfigurationWithAlternative(
                llmConfiguration,
                selectedAlternative,
                desiredModel,
                useAiGateway ?? false
            );

            llmConfigurationsField.onChange({
                ...llmConfigurationsField.value,
                [desiredModel]: updatedConfiguration,
            });

            notifyFieldChanges();
        }, [ llmConfigurationsField, notifyFieldChanges, useAiGateway ]);

    return (<>
        {Object.entries(llmConfigurationsField.value).map(([ desiredModel, llmConfiguration ]) => {
            const availableApiFlavors = enableByoForPassthrough
                ? (llmConfiguration.defaultModel && llmConfiguration.defaultModel !== llmConfiguration.desiredModel
                    ? getApiFlavorsForAlternative(
                        productConfigurations,
                        product,
                        feature,
                        llmConfiguration.defaultModel,
                        llmConfiguration.desiredModel ?? desiredModel
                    )
                    : getApiFlavorsForModel(
                        productConfigurations,
                        product,
                        feature,
                        llmConfiguration.defaultModel ?? desiredModel,
                        false
                    ))
                : [];

            return (<UiStack
                key={`llm-configuration-${desiredModel}`}
                data-cy={`llm-configuration-${desiredModel}`}
                className={classes.llmConfigurationsSection}
                gap={SpacingToken.S}
                pl={SpacingToken.M}
                w='100%'
                direction='column'
            >
                <UiStack
                    wrap='wrap'
                    gap={SpacingToken.M}
                    direction='row'
                >
                    {hasModelAlternatives(productConfigurations, product, feature, llmConfiguration.defaultModel ?? desiredModel) ? (
                        <ModelAlternativeDropdown
                            targetProduct={product}
                            targetFeature={feature}
                            targetModel={llmConfiguration.defaultModel ?? desiredModel}
                            productConfigurations={productConfigurations ?? {}}
                            value={getCurrentSelectedAlternative(llmConfiguration)}
                            onChange={handleModelAlternativeChange(desiredModel, llmConfiguration)}
                            disabled={readOnly}
                        />
                    ) : (
                        <ApTextField
                            style={{ width: '350px' }}
                            label={translate({ id: 'CLIENT_BYO_LLM_REPLACED_MODEL' })}
                            value={desiredModel}
                            required
                            readOnly
                            disabled={!!readOnly}
                            data-cy={`desired-model-text-field-${desiredModel}`}
                        />
                    )}
                    {useAiGateway && !(enableByoForPassthrough && availableApiFlavors.length > 0) && (
                        <AiGatewayDialectDropdown
                            value={llmConfiguration.gatewayParameters?.vendorApiType}
                            disabled={!!readOnly || !desiredModel}
                            availableDialects={getAvailableDialectsForModel(llmConfiguration.desiredModel ?? desiredModel, llmToConnectorTypeMapping)}
                            llmConfigurationsField={llmConfigurationsField}
                            notifyFieldChanges={notifyFieldChanges}
                            desiredModel={desiredModel}
                            llmConfiguration={llmConfiguration}
                        />
                    )}
                    {enableByoForPassthrough && availableApiFlavors.length > 0 && (
                        <LlmApiFlavorDropdown
                            value={llmConfiguration.apiFlavor}
                            disabled={!!readOnly || (useAiGateway
                                ? !llmConfiguration.gatewayParameters?.authConnectorType
                                : !llmConfiguration.connectorType)}
                            availableApiFlavors={availableApiFlavors}
                            llmConfigurationsField={llmConfigurationsField}
                            notifyFieldChanges={notifyFieldChanges}
                            desiredModel={desiredModel}
                            llmConfiguration={llmConfiguration}
                        />
                    )}
                </UiStack>
                <UiStack
                    wrap='wrap'
                    gap={SpacingToken.M}
                    direction='row'>
                    <ConnectorDropdown
                        organizationId={organizationId}
                        tenantId={tenantId}
                        label={useAiGateway
                            ? translate({ id: 'CLIENT_CG_AUTH_CONNECTOR' })
                            : translate({ id: 'CLIENT_CG_CONNECTOR' })}
                        required
                        disabled={readOnly}
                        filter={connectorKey => {
                            if (useAiGateway) {
                                return connectorKey in IntegrationServiceConnectorTypesToAuthConnectorTypes
                                    || (isAiGatewayCustomAuthConnector(connectorKey))
                                    || byoAiGatewayCustomConnectors.includes(connectorKey);
                            }

                            const isLlmVendorConnector = connectorKey in IntegrationServiceConnectorTypesToLlmConnectorTypes;

                            if (!isLlmVendorConnector) {
                                return false;
                            }

                            return llmToConnectorTypeMapping[llmConfiguration.desiredModel ?? desiredModel]
                                .includes(IntegrationServiceConnectorTypesToLlmConnectorTypes[connectorKey]);
                        }}
                        value={
                            useAiGateway
                                ? llmConfiguration.gatewayParameters?.authConnectorType
                                : getLlmConnectorTypeIntegrationServiceEquivalent(llmConfiguration.connectorType)
                        }
                        onChange={(value) => {
                            if (useAiGateway) {
                                llmConfiguration.gatewayParameters = {
                                    apiUrl: '',
                                    authConnectionId: '',
                                    authConnectorType: value ?? '',
                                    vendorApiType: llmConfiguration.gatewayParameters?.vendorApiType ?? ModelFamily.OpenAi,
                                };
                            } else {
                                llmConfiguration.connectorType = IntegrationServiceConnectorTypesToLlmConnectorTypes[value ?? ''];
                                llmConfiguration.connectionId = '';
                            }

                            llmConfiguration.customerModel = '';
                            llmConfigurationsField.onChange({
                                ...llmConfigurationsField.value,
                                [desiredModel]: llmConfiguration,
                            });

                            notifyFieldChanges();
                        }} />
                    <ConnectionDropdown
                        organizationId={organizationId}
                        tenantId={tenantId}
                        folderKey={folderKey}
                        connectorKey={
                            useAiGateway
                                ? llmConfiguration.gatewayParameters?.authConnectorType
                                : getLlmConnectorTypeIntegrationServiceEquivalent(llmConfiguration.connectorType)
                        }
                        label={translate({ id: 'CLIENT_CG_CONNECTION' })}
                        required
                        disabled={
                            !!readOnly || (useAiGateway
                                ? !llmConfiguration.gatewayParameters?.authConnectorType
                                : !llmConfiguration.connectorType)
                        }
                        value={
                            useAiGateway
                                ? llmConfiguration.gatewayParameters?.authConnectionId
                                : llmConfiguration.connectionId
                        }
                        onChange={(value) => {
                            if (useAiGateway) {
                                llmConfiguration.gatewayParameters = {
                                    apiUrl: llmConfiguration.gatewayParameters?.apiUrl ?? '',
                                    authConnectionId: value.id,
                                    authConnectorType: llmConfiguration.gatewayParameters?.authConnectorType ?? '',
                                    vendorApiType: llmConfiguration.gatewayParameters?.vendorApiType ?? ModelFamily.OpenAi,
                                };
                            } else {
                                llmConfiguration.connectionId = value.id;
                            }
                            llmConfiguration.customerModel = '';
                            llmConfigurationsField.onChange({
                                ...llmConfigurationsField.value,
                                [desiredModel]: llmConfiguration,
                            });

                            notifyFieldChanges();
                        }} />
                    {useAiGateway && (
                        <TextField
                            style={{ width: '300px' }}
                            label={translate({ id: 'CLIENT_BYO_LLM_BASE_URL' })}
                            data-cy='base-url-text-field'
                            required
                            disabled={!!readOnly || !llmConfiguration.gatewayParameters?.authConnectionId}
                            value={llmConfiguration.gatewayParameters?.apiUrl}
                            onChange={(event) => {
                                llmConfiguration.gatewayParameters = {
                                    apiUrl: event.target.value,
                                    authConnectionId: llmConfiguration?.gatewayParameters?.authConnectionId ?? '',
                                    authConnectorType: llmConfiguration?.gatewayParameters?.authConnectorType ?? '',
                                    vendorApiType: llmConfiguration.gatewayParameters?.vendorApiType ?? ModelFamily.OpenAi,
                                };
                                llmConfigurationsField.onChange({
                                    ...llmConfigurationsField.value,
                                    [desiredModel]: llmConfiguration,
                                });

                                notifyFieldChanges();
                            }} />
                    )}
                    <AvailableModelsAutocomplete
                        label={translate({ id: 'CLIENT_BYO_LLM_CONNECTED_MODEL_ID' })}
                        required
                        disabled={
                            !!readOnly || (useAiGateway
                                ? !llmConfiguration.gatewayParameters?.authConnectionId
                                : !llmConfiguration.connectionId)
                        }
                        organizationId={organizationId}
                        tenantId={tenantId}
                        byoLlmConfiguration={llmConfiguration}
                        initialValue={llmConfiguration.customerModel ?? ''}
                        onChange={(value) => {
                            llmConfiguration.customerModel = value ?? '';
                            llmConfigurationsField.onChange({
                                ...llmConfigurationsField.value,
                                [desiredModel]: llmConfiguration,
                            });

                            notifyFieldChanges();
                        }} />
                </UiStack>
            </UiStack>
            );
        })}
    </>);
};
